import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import { ContextMenu } from "@/components/ui";
import {
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/shadcn/ContextMenu/ContextMenuPrimitives";
import {
  Clipboard,
  FileText,
  Info,
  FilePenLine,
  Pin,
  PinOff,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { copyToClipboard } from "@/lib/utils/copyToClipboard";

interface MatterNoteContextMenuProps {
  canDelete: boolean;
  canEdit: boolean;
  copyValue: string;
  creationInfo: string;
  isDraft: boolean;
  isEditing: boolean;
  isPinned: boolean;
  isUpdating: boolean;
  massTortId?: string;
  matterId: string;
  noteId: string;
  onDelete: () => void;
  onDraftToggle: () => void;
  onEdit: () => void;
  onPinToggle: () => void;
  trigger: ReactNode;
}

export const MatterNoteContextMenu = ({
  canDelete,
  canEdit,
  copyValue,
  creationInfo,
  isDraft,
  isEditing,
  isPinned,
  isUpdating,
  massTortId,
  matterId,
  noteId,
  onDelete,
  onDraftToggle,
  onEdit,
  onPinToggle,
  trigger,
}: MatterNoteContextMenuProps) => {
  const [highlightedText, setHighlightedText] = useState("");
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);

  return (
    <>
      <ContextMenu
        disabled={isEditing}
        entityName="note"
        open={canEdit ? { onSelect: onEdit } : false}
        delete={canDelete ? { onSelect: onDelete } : false}
        trigger={trigger}
        onContextMenu={(event) => {
          const selection = window.getSelection();
          const isWithinNote =
            selection?.anchorNode &&
            selection.focusNode &&
            event.currentTarget.contains(selection.anchorNode) &&
            event.currentTarget.contains(selection.focusNode);

          setHighlightedText(isWithinNote ? selection.toString() : "");
        }}
      >
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <Clipboard />
            Copy
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {highlightedText.trim() ? (
              <ContextMenuItem
                onSelect={() => {
                  void copyToClipboard(highlightedText, "Highlighted text");
                }}
              >
                <Clipboard />
                Highlighted
              </ContextMenuItem>
            ) : null}
            <ContextMenuItem
              onSelect={() => {
                void copyToClipboard(copyValue, "Note contents");
              }}
            >
              <FileText />
              Contents
            </ContextMenuItem>
            <ContextMenuItem
              onSelect={() => {
                void copyToClipboard(
                  `${copyValue}\n\n${creationInfo}`,
                  "Note contents and creation info",
                );
              }}
            >
              <Info />
              Contents and creation info
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
        {canEdit ? (
          <>
            <ContextMenuItem disabled={isUpdating} onSelect={onPinToggle}>
              {isPinned ? <PinOff /> : <Pin />}
              {isPinned ? "Unpin note" : "Pin note"}
            </ContextMenuItem>
            <ContextMenuItem disabled={isUpdating} onSelect={onDraftToggle}>
              <FilePenLine />
              {isDraft ? "Publish note" : "Mark as draft"}
            </ContextMenuItem>
          </>
        ) : null}
        {!massTortId ? (
          <MatterSubItemEventLogsContextMenu
            onOpenEventLogs={() => {
              setIsEventLogsOpen(true);
            }}
          />
        ) : null}
      </ContextMenu>
      {!massTortId ? (
        <MatterSubItemEventLogsModal
          entityLabel="note"
          matter={{ id: matterId }}
          onOpenChange={setIsEventLogsOpen}
          open={isEventLogsOpen}
          subItem={{ id: noteId, type: "note" }}
        />
      ) : null}
    </>
  );
};
